import { useEffect, useState } from 'react'
import WarehouseActionModal from '@/components/WarehouseActionModal'
import WarningBanner from '@/components/WarningBanner'
import { useAuth } from '@/contexts/AuthContext'
import type { MonthlyPerformanceSettings } from '@/types/monthlyPerformance'
import EmailChipInput from './EmailChipInput'
import {
  defaultReportEmailSubject,
  MEMBERS_PRESET_RECIPIENTS,
  withSendDefaults,
} from './sendModalDefaults'
import { formatChicagoImportTimestamp } from './monthlyPerformanceUtils'
import type { MonthlyPerformanceSendLogEntry } from '@/types/monthlyPerformance'

export type SendEmailFormState = MonthlyPerformanceSettings['email']

type Preset = 'testing' | 'members'
type MembersStep = 'edit' | 'confirm'

const TEST_SEND_WARNING =
  'This is a test send. The report will not be marked as sent to members. Use the Members preset to send the report.'

type MonthlyPerformanceSendModalProps = {
  open: boolean
  periodLabel: string
  baseEmail: SendEmailFormState
  membersDisabled: boolean
  membersDisabledReason?: string
  sendLog: MonthlyPerformanceSendLogEntry[]
  onClose: () => void
  onSend: (email: SendEmailFormState, mode: 'test' | 'members') => void | Promise<void>
}

function emptyEmailForm(): SendEmailFormState {
  return {
    send_as: '',
    from_name: '',
    reply_to: null,
    subject_template: '',
    recipients: [],
    cc: [],
    test_recipient: null,
    attach_comparison_pdf: false,
  }
}

function PresetTag({
  label,
  selected,
  disabled,
  disabledReason,
  onClick,
}: {
  label: string
  selected: boolean
  disabled?: boolean
  disabledReason?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={disabled && disabledReason ? disabledReason : undefined}
      aria-label={disabled && disabledReason ? `${label}: ${disabledReason}` : label}
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 transition-colors ${
        selected
          ? 'bg-gray-100 text-gray-900 ring-gray-300'
          : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {label}
    </button>
  )
}

export default function MonthlyPerformanceSendModal({
  open,
  periodLabel,
  baseEmail,
  membersDisabled,
  membersDisabledReason,
  sendLog,
  onClose,
  onSend,
}: MonthlyPerformanceSendModalProps) {
  const { user } = useAuth()
  const userEmail = user?.email ?? ''
  const [email, setEmail] = useState<SendEmailFormState>(baseEmail)
  const [sendMode, setSendMode] = useState<'test' | 'members'>('test')
  const [activePreset, setActivePreset] = useState<Preset | null>('testing')
  const [membersStep, setMembersStep] = useState<MembersStep>('edit')
  useEffect(() => {
    if (!open) return
    const emailBase = withSendDefaults(baseEmail)
    const memberSubject = defaultReportEmailSubject(periodLabel)
    setSendMode('test')
    setActivePreset('testing')
    setMembersStep('edit')
    setEmail({
      ...emailBase,
      subject_template: `[TEST] ${memberSubject}`,
      recipients: userEmail ? [userEmail] : [],
      cc: [],
    })
  }, [baseEmail, open, periodLabel, userEmail])

  if (!open) return null

  const clearAllFields = () => {
    setActivePreset(null)
    setSendMode('test')
    setMembersStep('edit')
    setEmail(emptyEmailForm())
  }

  const applyPreset = (preset: Preset) => {
    if (activePreset === preset) {
      clearAllFields()
      return
    }
    if (preset === 'members' && membersDisabled) return
    setActivePreset(preset)
    setMembersStep('edit')
    const emailBase = withSendDefaults(baseEmail)
    const memberSubject = defaultReportEmailSubject(periodLabel)
    if (preset === 'testing') {
      setSendMode('test')
      setEmail({
        ...emailBase,
        recipients: userEmail ? [userEmail] : [],
        cc: [],
        subject_template: `[TEST] ${memberSubject}`,
      })
      return
    }
    setSendMode('members')
    setEmail({
      ...emailBase,
      subject_template: memberSubject,
      recipients: [...MEMBERS_PRESET_RECIPIENTS],
      cc: [],
    })
  }

  const handleSubmit = () => {
    if (sendMode === 'members' && membersStep === 'edit') {
      setMembersStep('confirm')
      return
    }
    void onSend(email, sendMode)
  }

  const memberCount = email.recipients.length
  const submitLabel =
    sendMode === 'members' && membersStep === 'confirm'
      ? `Send to ${memberCount} member${memberCount === 1 ? '' : 's'}`
      : sendMode === 'members'
        ? 'Continue'
        : 'Send'

  const showFormFields =
    !(sendMode === 'members' && membersStep === 'confirm')

  const showTestRecipientWarning =
    sendMode === 'test' &&
    email.recipients.length > 0 &&
    email.recipients.some(
      (recipient) => recipient.toLowerCase() !== userEmail.toLowerCase(),
    )

  return (
    <>
      <WarehouseActionModal
        title="Send report"
        wide
        titleDivider
        submitLabel={submitLabel}
        submitDisabled={sendMode === 'members' && membersStep === 'confirm' && memberCount === 0}
        onClose={onClose}
        onBack={sendMode === 'members' && membersStep === 'confirm' ? () => setMembersStep('edit') : undefined}
        onSubmit={handleSubmit}
      >
        {sendLog.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
            <p className="font-medium text-gray-900">Send log for this report month</p>
            <ul className="mt-2 max-h-40 space-y-2 overflow-y-auto">
              {sendLog.map((entry) => (
                <li key={entry.id} className="border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">
                        #{entry.report_id}{' '}
                        {entry.mode === 'members' ? 'Members' : 'Test'}
                      </span>
                      <span className="text-gray-600">
                        {' '}
                        · {formatChicagoImportTimestamp(entry.sent_at)}
                        {entry.error ? ' · failed' : ''}
                      </span>
                      <p className="mt-0.5 text-xs text-gray-600">
                        To: {(entry.to ?? []).join(', ') || '—'}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {showTestRecipientWarning ? <WarningBanner message={TEST_SEND_WARNING} /> : null}
        {showFormFields ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-sm font-medium text-gray-700">Presets</span>
            <div className="flex flex-wrap gap-2">
              <PresetTag
                label="Test (me)"
                selected={activePreset === 'testing'}
                onClick={() => applyPreset('testing')}
              />
              <PresetTag
                label="Members"
                selected={activePreset === 'members'}
                disabled={membersDisabled}
                disabledReason={membersDisabledReason}
                onClick={() => applyPreset('members')}
              />
            </div>
          </div>
        ) : null}
        {sendMode === 'members' && membersStep === 'confirm' ? (
          <div className="space-y-3 text-sm text-gray-800">
            <div>
              <p className="font-medium text-gray-700">To</p>
              <p className="mt-1">{email.recipients.join(', ') || '—'}</p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Cc</p>
              <p className="mt-1">{email.cc.length > 0 ? email.cc.join(', ') : '—'}</p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Subject</p>
              <p className="mt-1">{email.subject_template}</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-gray-700">Send as</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={email.send_as}
                onChange={(e) => setEmail({ ...email, send_as: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">From name</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={email.from_name}
                onChange={(e) => setEmail({ ...email, from_name: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Reply to</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={email.reply_to ?? ''}
                onChange={(e) =>
                  setEmail({ ...email, reply_to: e.target.value || null })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={email.subject_template}
                onChange={(e) => setEmail({ ...email, subject_template: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <EmailChipInput
                id="send-modal-to"
                label="To"
                values={email.recipients}
                onChange={(recipients) => setEmail({ ...email, recipients })}
              />
            </div>
            <div className="md:col-span-2">
              <EmailChipInput
                id="send-modal-cc"
                label="Cc"
                values={email.cc}
                onChange={(cc) => setEmail({ ...email, cc })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-800 md:col-span-2">
              <input
                type="checkbox"
                checked={email.attach_comparison_pdf ?? false}
                onChange={(e) =>
                  setEmail({ ...email, attach_comparison_pdf: e.target.checked })
                }
              />
              Attach comparison PDF
            </label>
          </div>
        )}
      </WarehouseActionModal>
    </>
  )
}
